/**
 * 当前用户开放(endedAt=null)的 task work session — 用于:
 * ① 显示运行中的 Work Type(special)并提供 End 入口;
 * ② clock-out / break 的前置提示(后端 OPEN_WORK_SESSION_EXISTS 的可视化)。
 * 查询形状对齐 mobile reconcileService.fetchOpenWorkSessions。
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { workSessions } from "@/services/workSessionService";
import type { BaseQuery } from "@/types/baseQuery";
import type { SnowflakeId } from "@/types/snowflake";
import type { OpenSessionInfo } from "./derive";

import { useRefreshSignal } from "@/features/realtime/useRefreshSignal";

const OPEN_SESSION_COLUMNS =
  "id taskId startedAt task.id task.title task.taskTypeCode task.status";

export function useOpenSessions(userId: SnowflakeId | null) {
  const [openSessions, setOpenSessions] = useState<OpenSessionInfo[]>([]);
  // 首拉是否已完成(成功或降级)。消费方必须等它为 true 才能把「openSessions 为空」
  // 当成「没有开放 session」——慢网下 sessions 后到会让跨天补签退的默认时刻停在
  // clock_in，整段班被补签成 1 秒(2026-07-29 prod 事故路径)。
  const [loaded, setLoaded] = useState(false);
  const sequence = useRef(0);
  useEffect(() => () => { sequence.current++; }, [userId]);

  const refresh = useCallback(async () => {
    const seq = ++sequence.current;
    if (!userId) {
      setOpenSessions([]);
      setLoaded(true);
      return;
    }
    try {
      const res = await workSessions.search({
        filters: {
          assigneeId: { type: "string", operator: "equals", value: userId },
          endedAt: { type: "date", operator: "is_null" },
        },
        orderBy: "-startedAt",
        page: 1,
        pageSize: 10,
        columns: OPEN_SESSION_COLUMNS,
      } as unknown as BaseQuery);
      if (seq !== sequence.current) return;
      const rows = (res?.results ?? []) as Array<Record<string, any>>;
      setOpenSessions(
        rows.map((r) => ({
          sessionId: String(r.id),
          taskId: String(r.taskId ?? r.task?.id ?? ""),
          title: String(r.task?.title ?? ""),
          taskTypeCode: String(r.task?.taskTypeCode ?? ""),
          startedAt: String(r.startedAt ?? ""),
        })),
      );
    } catch (e) {
      // 探测失败退化为空(按钮回到可点,真冲突后端仍会 409 兜底)
      console.error("[useOpenSessions] task-work-sessions search failed:", e);
      if (seq === sequence.current) setOpenSessions([]);
      return false;
    } finally {
      // 失败也置 loaded:降级到"无开放 session"是既有行为,不能让 UI 永远卡在 loading。
      if (seq === sequence.current) setLoaded(true);
    }
  }, [userId]);

  useRefreshSignal({ resource: "myAttendance", enabled: !!userId, onRefresh: refresh });
  return { openSessions, loaded, refresh };
}
